import { IEditorV3, isIEditorV3 } from "@azu-business/editor-v3";
import {
  ArsTable,
  ArsTableBody,
  ArsTableRow,
  IRequestStatus,
  isArsTableBody,
  loadWrapper,
  MultipleDatasetCheck,
  SET_SIDEBAR_ANALYSIS_METHOD_ID,
  SET_VARIABLES_IN_USE,
  useArsContext,
} from "aic";
import { CommentScope } from "components/common/comments/CommentScope";
import { v3EditorProps } from "components/common/v3EditorProps";
import { useAppSettings, useTlfContext } from "contexts/AppSettingsContext";
import { UPDATE_TLF } from "contexts/tlf/interface";
import {
  ArsCalculationDto,
  getArsTableCalculation,
} from "functions/api/tlfItem/getArsTableCalculation";
import { isTlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { startTransition, useEffect, useEffectEvent, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ArsItemRequestingSpinner } from "./ArsItemRequestingSpinner";
import { ArsSetPicker } from "./ArsSetPicker";
import { TlfArsPatchSidebar } from "./variants/TlfArsPatchSidebar";

/**
 * Wrapper component for ArsTable.
 */
export const TlfArsTable = () => {
  // Contexts
  const appSettings = useAppSettings();
  const tlfContext = useTlfContext();
  const tlfData = tlfContext.tlfData;
  const arsContext = useArsContext();
  const hierarchyId = appSettings.hierarchyState.hierarchy?.id;
  const primaryHierarchyId = tlfData?.primaryHierarchyId;

  // Derived from contexts
  const body =
    tlfContext && isTlfItemReadDto(tlfData) && isArsTableBody(tlfData.body, isIEditorV3)
      ? tlfData.body
      : null;
  const disabled = tlfContext.readOnly;

  // Component state items
  const [calculatedTableRows, setCalculatedTableRows] = useState<ArsTableRow<IEditorV3>[]>([]);
  const [requestingCalculation, setRequestingCalculation] = useState<
    IRequestStatus<ArsCalculationDto>
  >({
    requesting: false,
    error: false,
  });
  const [errorText, setErrorText] = useState<string | null>(null);

  const getCalculatedRows = useEffectEvent(async (body: ArsTableBody<IEditorV3>) => {
    const analysisSet = tlfData?.analysisSetId
      ? arsContext.state.analysisSets[tlfData.analysisSetId]
      : undefined;
    const dataSubset =
      body.dataSubsetId && Object.keys(arsContext.state.dataSubsets).includes(body.dataSubsetId)
        ? arsContext.state.dataSubsets[body.dataSubsetId]
        : undefined;
    const arsCalculationDto: ArsCalculationDto = {
      arsTableBody: body,
      analysisSet,
      dataSubset,
    };
    const request = await loadWrapper(
      getArsTableCalculation,
      arsCalculationDto,
      { ...requestingCalculation, error: false },
      setRequestingCalculation,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (request?.success) {
      arsContext.dispatch({
        operation: SET_VARIABLES_IN_USE,
        variablesInUse: request.data.variables,
      });
      startTransition(() => {
        setCalculatedTableRows(request.data.rows);
        setErrorText(null);
      });
    } else if (request?.success === false) {
      startTransition(() => setErrorText(request.errorText));
    }
  });

  const checkAnalysisMethod = useEffectEvent(async (body: ArsTableBody<IEditorV3>) => {
    /* istanbul ignore else */
    if (body.analysisMethodId !== arsContext.state.currentSidebar.analysisMethodId)
      arsContext.dispatch({
        operation: SET_SIDEBAR_ANALYSIS_METHOD_ID,
        analysisMethodId: body.analysisMethodId,
      });
  });

  useEffect(() => {
    if (body && !tlfContext.requestStatus.requesting && primaryHierarchyId === hierarchyId) {
      getCalculatedRows(body);
      checkAnalysisMethod(body);
    }
  }, [body, hierarchyId, primaryHierarchyId, tlfContext.requestStatus.requesting]);

  return !tlfContext || !body || primaryHierarchyId !== hierarchyId ? null : (
    <Container
      fluid
      style={{ minHeight: "400px" }}
    >
      <Row>
        <Col className="d-flex align-items-start flex-row gap-1 py-3">
          <ArsSetPicker
            type={"analysisSet"}
            itemId={tlfData?.analysisSetId ?? ""}
            disabled={disabled}
            onChange={(newId) => {
              tlfContext.dispatch({
                operation: UPDATE_TLF,
                tlfData: {
                  analysisSetId: newId,
                },
                calledFrom: "TlfArsTable onChange:AnalysisSet",
              });
            }}
          />
          <ArsSetPicker
            type={"dataSubset"}
            itemId={body.dataSubsetId ?? ""}
            disabled={disabled}
            onChange={(newId) => {
              tlfContext.dispatch({
                operation: UPDATE_TLF,
                tlfData: {
                  body: {
                    ...body,
                    dataSubsetId: newId,
                  },
                },
                calledFrom: "TlfArsTable onChange:DataSubset",
              });
            }}
          />
          <MultipleDatasetCheck />
        </Col>
      </Row>
      {errorText && (
        <Row>
          <Col className="text-danger m-3">{errorText}</Col>
        </Row>
      )}
      <Row>
        <Col className="d-flex position-relative">
          <ArsItemRequestingSpinner
            requestingCalculation={requestingCalculation}
            requestingLabel="Recalculating"
          />
          <div className="m-auto px-3">
            <CommentScope
              id="ars-table-comment"
              activeContext="TlfItem"
              wrap
              iconPosition={{ top: "-16px", left: "-16px" }}
              commentLabel="New table comment"
            >
              <ArsTable<IEditorV3>
                arsTableBody={body}
                calculatedTableRows={calculatedTableRows}
                disabled={disabled}
                editorProps={v3EditorProps}
                sidebarStyle={{ paddingBottom: "40px" }}
                style={appSettings.defaultTableStyle}
                tlfViewMode={tlfContext.tlfViewMode}
                updateArsTableBody={(newBody, calculatedTableRows) => {
                  setCalculatedTableRows(calculatedTableRows);
                  tlfContext!.dispatch({
                    operation: UPDATE_TLF,
                    tlfData: { body: newBody },
                    calledFrom: "TlfArsTable onChange",
                  });
                }}
              />
            </CommentScope>
          </div>
          <TlfArsPatchSidebar />
        </Col>
      </Row>
    </Container>
  );
};

TlfArsTable.displayName = "TlfArsTable";
